import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { inventoryService, Item, Compartment } from '../services/inventoryService';
import { orderService } from '../services/orderService';
import { adminService, AvailableItem } from '../services/adminService';
import { useAuth } from '../context/AuthContext';
import ExpiryDateCell from '../components/ExpiryDateCell';
import ExpiryDialog from '../components/ExpiryDialog';
import { CompartmentItem, ItemInstance } from '../types/inventory';
import './ItemDetail.css';

const DEFAULT_STORAGE_LOCATIONS = ['Dachau', 'Indersdorf'];
const STORAGE_LOCATIONS_KEY = 'inventory-storage-locations';

interface SortableTableRowProps {
  id: string;
  children: (dragHandleProps: {
    attributes: ReturnType<typeof useSortable>['attributes'];
    listeners: ReturnType<typeof useSortable>['listeners'];
  }) => React.ReactNode;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.8 } : {}),
  };

  return <tr ref={setNodeRef} style={style}>{children({ attributes, listeners })}</tr>;
};

export default function ItemDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [availableItems, setAvailableItems] = useState<AvailableItem[]>([]);
  const [addModal, setAddModal] = useState<{ show: boolean; compId?: string; compName?: string; modulePackName?: string }>({ show: false });
  const [addForm, setAddForm] = useState<{ itemName: string; qty: number }>({ itemName: '', qty: 1 });
  const [itemSuggestions, setItemSuggestions] = useState<AvailableItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [newCompModal, setNewCompModal] = useState(false);
  const [newCompName, setNewCompName] = useState('');
  const [renameCompModal, setRenameCompModal] = useState<{ show: boolean; compId?: string; name?: string }>({ show: false });
  const [settingsModal, setSettingsModal] = useState(false);
  const [customLocations, setCustomLocations] = useState<string[]>([]);
  const [settingsForm, setSettingsForm] = useState<{ unitName: string; quantity: number; autoRunning: boolean; storageLocation: string; locationMode: 'shared' | 'individual'; instances: Array<{ id: number; type: string; identifier: string; storageLocation?: string }> }>({ unitName: '', quantity: 1, autoRunning: false, storageLocation: '', locationMode: 'shared', instances: [] });
  const [positionInputs, setPositionInputs] = useState<Record<string, Record<number, string>>>({});
  const [modulePackPositionInputs, setModulePackPositionInputs] = useState<Record<string, Record<number, string>>>({});
  const [addModulePackModal, setAddModulePackModal] = useState<{ show: boolean; compId?: string; compName?: string }>({ show: false });
  const [newModulePackName, setNewModulePackName] = useState('');
  const [renameModulePackModal, setRenameModulePackModal] = useState<{ show: boolean; compId?: string; oldName?: string; newName?: string }>({ show: false });
  const { isAdmin, user } = useAuth();
  const [orderModal, setOrderModal] = useState<{
    show: boolean;
    itemName: string;
    compartment?: string;
  }>({ show: false, itemName: '' });
  const [orderForm, setOrderForm] = useState({ quantity: 1, location: '', notes: '' });
  const addItemInputRef = useRef<HTMLInputElement>(null);
  const [editingQty, setEditingQty] = useState<Record<string, Record<number, boolean>>>({});
  const [qtyInputs, setQtyInputs] = useState<Record<string, Record<number, number>>>({});
  const [itemNameInputs, setItemNameInputs] = useState<Record<string, Record<number, string>>>({});
  const [editSuggestions, setEditSuggestions] = useState<AvailableItem[]>([]);
  const [showEditSuggestions, setShowEditSuggestions] = useState<string>('');
  const [editingModulePackQty, setEditingModulePackQty] = useState<Record<string, Record<number, boolean>>>({});
  const [modulePackQtyInputs, setModulePackQtyInputs] = useState<Record<string, Record<number, number>>>({});
  const [modulePackItemNameInputs, setModulePackItemNameInputs] = useState<Record<string, Record<number, string>>>({});
  const [expiryDialog, setExpiryDialog] = useState<{ 
    show: boolean; 
    compartmentId?: string; 
    itemIndex?: number; 
    item?: CompartmentItem;
    isModulePack?: boolean;
    modulePackName?: string;
  }>({ show: false });
  const [collapsedCompartments, setCollapsedCompartments] = useState<Record<string, boolean>>({});
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const getMainSortableId = (compartmentId: string, idx: number, contentItem: any) =>
    `main:${compartmentId}:${contentItem.itemId || contentItem.name}:${idx}`;

  const getModulePackSortableId = (compartmentId: string, modulePackName: string, idx: number, contentItem: any) =>
    `module:${compartmentId}:${modulePackName}:${contentItem.itemId || contentItem.name}:${idx}`;

  useEffect(() => {
    if (id) loadItem();
  }, [id]);

  useEffect(() => {
    if (addModal.show && addItemInputRef.current) {
      addItemInputRef.current.focus();
    }
  }, [addModal.show]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_LOCATIONS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setCustomLocations(parsed.filter((location) => typeof location === 'string' && location.trim()));
      }
    } catch (err) {
      console.warn('Konnte gespeicherte Standorte nicht laden', err);
    }
  }, []);

  useEffect(() => {
    const loadAvailable = async () => {
      if (!isAdmin) return;
      try {
        const data = await adminService.getAvailableItems();
        setAvailableItems(data);
      } catch (err) {
        console.warn('Konnte available items nicht laden', err);
      }
    };
    loadAvailable();
  }, [isAdmin]);

  const loadItem = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const data = await inventoryService.getItem(id);
      setItem(data);
      // Set default instance if available
      if (data.data.instances && data.data.instances.length > 0) {
        setSelectedInstance(data.data.instances[0].identifier);
      }
      // Initialize all compartments as collapsed
      if (data.compartments) {
        const initialCollapsed: Record<string, boolean> = {};
        data.compartments.forEach((comp: Compartment) => {
          initialCollapsed[comp.id] = true;
        });
        setCollapsedCompartments(initialCollapsed);
      }
    } catch (err) {
      setError('Fehler beim Laden des Items');
    } finally {
      setLoading(false);
    }
  };

  const toggleCompartment = (compartmentId: string) => {
    setCollapsedCompartments(prev => ({
      ...prev,
      [compartmentId]: !prev[compartmentId]
    }));
  };

  const allAreCollapsed = () => {
    if (!item?.compartments) return true;
    return item.compartments.every(comp => collapsedCompartments[comp.id]);
  };

  const toggleAllCompartments = () => {
    if (!item?.compartments) return;
    const shouldCollapse = !allAreCollapsed();
    const newState: Record<string, boolean> = {};
    item.compartments.forEach(comp => {
      newState[comp.id] = shouldCollapse;
    });
    setCollapsedCompartments(newState);
  };

  const handleOrderClick = (itemName: string, compartmentName?: string) => {
    setOrderModal({ show: true, itemName, compartment: compartmentName });
    setOrderForm({ quantity: 1, location: item?.data.bezeichnung || '', notes: '' });
  };

  const handleOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await orderService.createOrder({
        itemReference: {
          itemId: id,
          name: orderModal.itemName,
          compartment: orderModal.compartment
        },
        quantity: orderForm.quantity,
        location: orderForm.location,
        notes: orderForm.notes,
        instanceIdentifier: item?.data.instances ? selectedInstance : undefined
      });
      alert('Bestellung erfolgreich erstellt!');
      setOrderModal({ show: false, itemName: '' });
    } catch (err) {
      alert('Fehler beim Erstellen der Bestellung');
    }
  };

  const updateCompartmentItems = async (compartmentId: string, items: any[]) => {
    const comp = item?.compartments?.find((c) => c.id === compartmentId);
    if (!comp) return;
    const contents = { items, modulePacks: comp.contents.modulePacks };
    await inventoryService.updateCompartment(compartmentId, contents);
    // Update local state
    setItem((prev) => {
      if (!prev || !prev.compartments) return prev;
      const updatedCompartments = prev.compartments.map((c) =>
        c.id === compartmentId ? { ...c, contents } : c
      );
      return { ...prev, compartments: updatedCompartments } as Item;
    });
  };

  const updateCompartmentModulePacks = async (compartmentId: string, modulePacks: any[]) => {
    const comp = item?.compartments?.find((c) => c.id === compartmentId);
    if (!comp) return;
    const contents = { items: comp.contents.items, modulePacks };
    await inventoryService.updateCompartment(compartmentId, contents);
    // Update local state
    setItem((prev) => {
      if (!prev || !prev.compartments) return prev;
      const updatedCompartments = prev.compartments.map((c) =>
        c.id === compartmentId ? { ...c, contents } : c
      );
      return { ...prev, compartments: updatedCompartments } as Item;
    });
  };

  const handleCompartmentDragEnd = async (compartmentId: string, items: any[], event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sortableIds = items.map((contentItem, idx) => getMainSortableId(compartmentId, idx, contentItem));
    const oldIndex = sortableIds.indexOf(String(active.id));
    const newIndex = sortableIds.indexOf(String(over.id));

    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;

    const reorderedItems = arrayMove(items, oldIndex, newIndex);
    await updateCompartmentItems(compartmentId, reorderedItems);
  };

  const handleModulePackDragEnd = async (
    compartmentId: string,
    modulePackName: string,
    modulePackItems: any[],
    event: DragEndEvent
  ) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sortableIds = modulePackItems.map((contentItem, idx) =>
      getModulePackSortableId(compartmentId, modulePackName, idx, contentItem)
    );
    const oldIndex = sortableIds.indexOf(String(active.id));
    const newIndex = sortableIds.indexOf(String(over.id));

    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;

    const comp = item?.compartments?.find((c) => c.id === compartmentId);
    if (!comp) return;

    const reorderedItems = arrayMove(modulePackItems, oldIndex, newIndex);
    const updatedModulePacks = (comp.contents.modulePacks || []).map((modulePack) =>
      modulePack.name === modulePackName
        ? { ...modulePack, items: reorderedItems }
        : modulePack
    );

    await updateCompartmentModulePacks(compartmentId, updatedModulePacks);
  };

  const handleDeleteItem = async (compartmentId: string, idx: number) => {
    if (!isAdmin) return;
    try {
      const comp = item?.compartments?.find((c) => c.id === compartmentId);
      if (!comp) return;
      const items = comp.contents.items.filter((_, i) => i !== idx);
      await updateCompartmentItems(compartmentId, items);
    } catch (err) {
      alert('Fehler beim Löschen');
    }
  };

  const handleEditQty = (compartmentId: string, idx: number, currentQty: number | string, currentName: string) => {
    setEditingQty((prev) => ({
      ...prev,
      [compartmentId]: { ...prev[compartmentId], [idx]: true }
    }));
    setQtyInputs((prev) => ({
      ...prev,
      [compartmentId]: { ...prev[compartmentId], [idx]: typeof currentQty === 'string' ? parseInt(currentQty) : currentQty }
    }));
    setItemNameInputs((prev) => ({
      ...prev,
      [compartmentId]: { ...prev[compartmentId], [idx]: currentName }
    }));
  };

  const handleEditItemNameChange = (compartmentId: string, idx: number, value: string) => {
    setItemNameInputs((prev) => ({
      ...prev,
      [compartmentId]: { ...prev[compartmentId], [idx]: value }
    }));
    const q = value.trim().toLowerCase();
    if (!q) {
      setEditSuggestions([]);
      setShowEditSuggestions('');
      return;
    }
    const matches = availableItems.filter((it) =>
      it.name.toLowerCase().includes(q) || it.id.toLowerCase().includes(q)
    ).slice(0, 10);
    setEditSuggestions(matches);
    setShowEditSuggestions(`${compartmentId}-${idx}`);
  };

  const handleSelectEditSuggestion = (compartmentId: string, idx: number, itemName: string) => {
    setItemNameInputs((prev) => ({
      ...prev,
      [compartmentId]: { ...prev[compartmentId], [idx]: itemName }
    }));
    setShowEditSuggestions('');
  };

  const handleSaveQty = async (compartmentId: string, idx: number) => {
    if (!isAdmin) return;
    const comp = item?.compartments?.find((c) => c.id === compartmentId);
    if (!comp) return;
    const newQty = qtyInputs[compartmentId]?.[idx];
    const newName = itemNameInputs[compartmentId]?.[idx];
    if (!newQty || newQty < 1) {
      alert('Bitte geben Sie eine gültige Menge ein (mindestens 1)');
      return;
    }
    if (!newName || !newName.trim()) {
      alert('Bitte geben Sie einen Item-Namen ein');
      return;
    }
    const items = [...comp.contents.items];
    items[idx] = { ...items[idx], name: newName.trim(), qty: newQty };
    try {
      await updateCompartmentItems(compartmentId, items);
      setEditingQty((prev) => ({
        ...prev,
        [compartmentId]: { ...prev[compartmentId], [idx]: false }
      }));
      setShowEditSuggestions('');
    } catch (err) {
      alert('Fehler beim Aktualisieren der Menge');
    }
  };

  const handleCancelEditQty = (compartmentId: string, idx: number) => {
    setEditingQty((prev) => ({
      ...prev,
      [compartmentId]: { ...prev[compartmentId], [idx]: false }
    }));
    setShowEditSuggestions('');
  };

  const handleExpiryClick = (compartmentId: string, itemIndex: number, item: CompartmentItem, isModulePack: boolean = false, modulePackName?: string) => {
    setExpiryDialog({
      show: true,
      compartmentId,
      itemIndex,
      item,
      isModulePack,
      modulePackName
    });
  };

  const handleExpirySave = async (instances: ItemInstance[]) => {
    const { compartmentId, itemIndex, isModulePack, modulePackName } = expiryDialog;
    if (!compartmentId || itemIndex === undefined) return;

    const nextExpiry = instances
      .map((instance) => instance.expiryDate)
      .filter((date): date is string => Boolean(date))
      .sort()[0] || null;

    const comp = item?.compartments?.find((c) => c.id === compartmentId);
    if (!comp) return;

    try {
      if (isModulePack && modulePackName) {
        // Update item in module pack
        const modulePacks = comp.contents.modulePacks?.map(mp => {
          if (mp.name === modulePackName) {
            const items = [...mp.items];
            items[itemIndex] = { ...items[itemIndex], instances, nextExpiry };
            return { ...mp, items };
          }
          return mp;
        });
        await updateCompartmentModulePacks(compartmentId, modulePacks || []);
      } else {
        // Update regular compartment item
        const items = [...comp.contents.items];
        items[itemIndex] = { ...items[itemIndex], instances, nextExpiry };
        await updateCompartmentItems(compartmentId, items);
      }
      setExpiryDialog({ show: false });
    } catch (err) {
      alert('Fehler beim Aktualisieren der Ablaufdaten');
    }
  };

  const handleEditModulePackQty = (compartmentId: string, modulePackName: string, idx: number, currentQty: number | string, currentName: string) => {
    const mpKey = `${compartmentId}-${modulePackName}`;
    setEditingModulePackQty((prev) => ({
      ...prev,
      [mpKey]: { ...prev[mpKey], [idx]: true }
    }));
    setModulePackQtyInputs((prev) => ({
      ...prev,
      [mpKey]: { ...prev[mpKey], [idx]: typeof currentQty === 'string' ? parseInt(currentQty) : currentQty }
    }));
    setModulePackItemNameInputs((prev) => ({
      ...prev,
      [mpKey]: { ...prev[mpKey], [idx]: currentName }
    }));
  };

  const handleEditModulePackItemNameChange = (compartmentId: string, modulePackName: string, idx: number, value: string) => {
    const mpKey = `${compartmentId}-${modulePackName}`;
    setModulePackItemNameInputs((prev) => ({
      ...prev,
      [mpKey]: { ...prev[mpKey], [idx]: value }
    }));
    const q = value.trim().toLowerCase();
    if (!q) {
      setEditSuggestions([]);
      setShowEditSuggestions('');
      return;
    }
    const matches = availableItems.filter((it) =>
      it.name.toLowerCase().includes(q) || it.id.toLowerCase().includes(q)
    ).slice(0, 10);
    setEditSuggestions(matches);
    setShowEditSuggestions(`${mpKey}-${idx}`);
  };

  const handleSelectEditModulePackSuggestion = (compartmentId: string, modulePackName: string, idx: number, itemName: string) => {
    const mpKey = `${compartmentId}-${modulePackName}`;
    setModulePackItemNameInputs((prev) => ({
      ...prev,
      [mpKey]: { ...prev[mpKey], [idx]: itemName }
    }));
    setShowEditSuggestions('');
  };

  const handleSaveModulePackQty = async (compartmentId: string, modulePackName: string, idx: number) => {
    if (!isAdmin) return;
    const comp = item?.compartments?.find((c) => c.id === compartmentId);
    if (!comp) return;
    const mpKey = `${compartmentId}-${modulePackName}`;
    const newQty = modulePackQtyInputs[mpKey]?.[idx];
    const newName = modulePackItemNameInputs[mpKey]?.[idx];
    if (!newQty || newQty < 1) {
      alert('Bitte geben Sie eine gültige Menge ein (mindestens 1)');
      return;
    }
    if (!newName || !newName.trim()) {
      alert('Bitte geben Sie einen Item-Namen ein');
      return;
    }
    const modulePacks = comp.contents.modulePacks?.map((mp) => {
      if (mp.name === modulePackName) {
        const items = [...mp.items];
        items[idx] = { ...items[idx], name: newName.trim(), qty: newQty };
        return { ...mp, items };
      }
      return mp;
    }) || [];
    try {
      await updateCompartmentModulePacks(compartmentId, modulePacks);
      setEditingModulePackQty((prev) => ({
        ...prev,
        [mpKey]: { ...prev[mpKey], [idx]: false }
      }));
      setShowEditSuggestions('');
    } catch (err) {
      alert('Fehler beim Aktualisieren der Menge');
    }
  };

  const handleCancelEditModulePackQty = (compartmentId: string, modulePackName: string, idx: number) => {
    const mpKey = `${compartmentId}-${modulePackName}`;
    setEditingModulePackQty((prev) => ({
      ...prev,
      [mpKey]: { ...prev[mpKey], [idx]: false }
    }));
    setShowEditSuggestions('');
  };

  const handleMoveItem = async (compartmentId: string, idx: number, direction: 'up' | 'down') => {
    if (!isAdmin) return;
    const comp = item?.compartments?.find((c) => c.id === compartmentId);
    if (!comp) return;
    const items = [...comp.contents.items];
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    try {
      await updateCompartmentItems(compartmentId, items);
    } catch (err) {
      alert('Fehler beim Verschieben');
    }
  };

  const handleMoveToPosition = async (compartmentId: string, idx: number, targetPos: string) => {
    if (!isAdmin) return;
    const comp = item?.compartments?.find((c) => c.id === compartmentId);
    if (!comp) return;
    const pos = parseInt(targetPos);
    if (isNaN(pos) || pos < 1 || pos > comp.contents.items.length) {
      alert(`Bitte geben Sie eine Position zwischen 1 und ${comp.contents.items.length} ein`);
      return;
    }
    const newIdx = pos - 1;
    if (newIdx === idx) return;
    const items = [...comp.contents.items];
    const [movedItem] = items.splice(idx, 1);
    items.splice(newIdx, 0, movedItem);
    try {
      await updateCompartmentItems(compartmentId, items);
      setPositionInputs((prev) => ({
        ...prev,
        [compartmentId]: { ...prev[compartmentId], [idx]: '' }
      }));
    } catch (err) {
      alert('Fehler beim Verschieben');
    }
  };

  const handleOpenAdd = (compId: string, compName: string, modulePackName?: string) => {
    setAddForm({ itemName: '', qty: 1 });
    setItemSuggestions([]);
    setShowSuggestions(false);
    setAddModal({ show: true, compId, compName, modulePackName });
  };

  const handleItemNameChange = (value: string) => {
    setAddForm({ ...addForm, itemName: value });
    const q = value.trim().toLowerCase();
    if (!q) {
      setItemSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const matches = availableItems.filter((it) =>
      it.name.toLowerCase().includes(q) || it.id.toLowerCase().includes(q)
    ).slice(0, 10);
    setItemSuggestions(matches);
    setShowSuggestions(matches.length > 0);
  };

  const handleSelectSuggestion = (item: AvailableItem) => {
    setAddForm({ ...addForm, itemName: item.name });
    setShowSuggestions(false);
  };

  const handleDeleteModulePackItem = async (compartmentId: string, modulePackName: string, itemIdx: number) => {
    if (!isAdmin) return;
    try {
      const comp = item?.compartments?.find((c) => c.id === compartmentId);
      if (!comp || !comp.contents.modulePacks) return;
      const updatedModulePacks = comp.contents.modulePacks.map((mp: any) => {
        if (mp.name === modulePackName) {
          return { ...mp, items: mp.items.filter((_: any, i: number) => i !== itemIdx) };
        }
        return mp;
      });
      await updateCompartmentModulePacks(compartmentId, updatedModulePacks);
    } catch (err) {
      alert('Fehler beim Löschen');
    }
  };

  const handleMoveModulePackItem = async (compartmentId: string, modulePackName: string, idx: number, direction: 'up' | 'down') => {
    if (!isAdmin) return;
    const comp = item?.compartments?.find((c) => c.id === compartmentId);
    if (!comp || !comp.contents.modulePacks) return;
    const modulePack = comp.contents.modulePacks.find((mp: any) => mp.name === modulePackName);
    if (!modulePack) return;
    const items = [...modulePack.items];
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    try {
      const updatedModulePacks = comp.contents.modulePacks.map((mp: any) => {
        if (mp.name === modulePackName) {
          return { ...mp, items };
        }
        return mp;
      });
      await updateCompartmentModulePacks(compartmentId, updatedModulePacks);
    } catch (err) {
      alert('Fehler beim Verschieben');
    }
  };

  const handleMoveModulePackItemToPosition = async (compartmentId: string, modulePackName: string, idx: number, targetPos: string) => {
    if (!isAdmin) return;
    const comp = item?.compartments?.find((c) => c.id === compartmentId);
    if (!comp || !comp.contents.modulePacks) return;
    const modulePack = comp.contents.modulePacks.find((mp: any) => mp.name === modulePackName);
    if (!modulePack) return;
    const pos = parseInt(targetPos);
    if (isNaN(pos) || pos < 1 || pos > modulePack.items.length) {
      alert(`Bitte geben Sie eine Position zwischen 1 und ${modulePack.items.length} ein`);
      return;
    }
    const newIdx = pos - 1;
    if (newIdx === idx) return;
    const items = [...modulePack.items];
    const [movedItem] = items.splice(idx, 1);
    items.splice(newIdx, 0, movedItem);
    try {
      const updatedModulePacks = comp.contents.modulePacks.map((mp: any) => {
        if (mp.name === modulePackName) {
          return { ...mp, items };
        }
        return mp;
      });
      await updateCompartmentModulePacks(compartmentId, updatedModulePacks);
      const mpKey = `${compartmentId}-${modulePackName}`;
      setModulePackPositionInputs((prev) => ({
        ...prev,
        [mpKey]: { ...prev[mpKey], [idx]: '' }
      }));
    } catch (err) {
      alert('Fehler beim Verschieben');
    }
  };

  const handleAddModulePack = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addModulePackModal.compId || !newModulePackName.trim()) return;
    try {
      const comp = item?.compartments?.find((c) => c.id === addModulePackModal.compId);
      if (!comp) return;
      const modulePacks = [...(comp.contents.modulePacks || []), { name: newModulePackName.trim(), items: [] }];
      await updateCompartmentModulePacks(addModulePackModal.compId, modulePacks);
      setAddModulePackModal({ show: false });
      setNewModulePackName('');
    } catch (err) {
      alert('Fehler beim Anlegen der Modultasche');
    }
  };

  const handleDeleteModulePack = async (compartmentId: string, modulePackName: string) => {
    if (!isAdmin) return;
    if (!confirm(`Modultasche "${modulePackName}" wirklich löschen?`)) return;
    try {
      const comp = item?.compartments?.find((c) => c.id === compartmentId);
      if (!comp || !comp.contents.modulePacks) return;
      const modulePacks = comp.contents.modulePacks.filter((mp: any) => mp.name !== modulePackName);
      await updateCompartmentModulePacks(compartmentId, modulePacks);
    } catch (err) {
      alert('Fehler beim Löschen der Modultasche');
    }
  };

  const handleRenameModulePackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!renameModulePackModal.compId || !renameModulePackModal.oldName || !renameModulePackModal.newName?.trim()) return;
    try {
      const comp = item?.compartments?.find((c) => c.id === renameModulePackModal.compId);
      if (!comp || !comp.contents.modulePacks) return;
      const modulePacks = comp.contents.modulePacks.map((mp: any) => {
        if (mp.name === renameModulePackModal.oldName) {
          return { ...mp, name: renameModulePackModal.newName!.trim() };
        }
        return mp;
      });
      await updateCompartmentModulePacks(renameModulePackModal.compId, modulePacks);
      setRenameModulePackModal({ show: false });
    } catch (err) {
      alert('Fehler beim Umbenennen der Modultasche');
    }
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addModal.compId) return;
    if (!addForm.itemName.trim() || addForm.qty < 1) {
      alert('Bitte Item-Name und Menge angeben');
      return;
    }
    const comp = item?.compartments?.find((c) => c.id === addModal.compId);
    if (!comp) return;
    
    try {
      // Check if item exists
      let availableItem = availableItems.find(
        (ai) => ai.name.toLowerCase() === addForm.itemName.trim().toLowerCase()
      );
      
      // If item doesn't exist, create it
      if (!availableItem) {
        try {
          availableItem = await adminService.createAvailableItem(addForm.itemName.trim());
          setAvailableItems((prev) => [...prev, availableItem!].sort((a, b) => a.name.localeCompare(b.name, 'de')));
        } catch (err) {
          alert('Fehler beim Anlegen des neuen Items');
          return;
        }
      }
      
      const newItem = { 
        itemId: availableItem.id, 
        name: availableItem.name, 
        qty: addForm.qty 
      };
      
      // If adding to a module pack
      if (addModal.modulePackName) {
        const updatedModulePacks = comp.contents.modulePacks?.map((mp: any) => {
          if (mp.name === addModal.modulePackName) {
            return { ...mp, items: [...(mp.items || []), newItem] };
          }
          return mp;
        }) || [];
        await updateCompartmentModulePacks(addModal.compId, updatedModulePacks);
      } else {
        // Adding to regular compartment
        const items = [...(comp.contents.items || []), newItem];
        await updateCompartmentItems(addModal.compId, items);
      }
      
      setAddModal({ show: false });
    } catch (err) {
      alert('Fehler beim Hinzufügen');
    }
  };

  const handleCreateCompartment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCompName.trim() || !id) return;
    try {
      const newComp = await inventoryService.createCompartment(id, newCompName.trim());
      setItem((prev) => {
        if (!prev) return prev;
        const compartments = [...(prev.compartments || []), newComp];
        return { ...prev, compartments } as Item;
      });
      setNewCompModal(false);
      setNewCompName('');
    } catch (err) {
      alert('Fehler beim Anlegen des Fachs');
    }
  };

  const handleDeleteCompartment = async (compartmentId: string, compartmentName: string) => {
    if (!isAdmin) return;
    if (!window.confirm(`Fach "${compartmentName}" wirklich löschen?`)) return;
    try {
      await inventoryService.deleteCompartment(compartmentId);
      setItem((prev) => {
        if (!prev || !prev.compartments) return prev;
        const compartments = prev.compartments.filter((c) => c.id !== compartmentId);
        return { ...prev, compartments } as Item;
      });
    } catch (err) {
      alert('Fehler beim Löschen des Fachs');
    }
  };

  const handleRenameCompartmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!renameCompModal.compId || !renameCompModal.name?.trim()) return;
    try {
      const renamed = await inventoryService.renameCompartment(renameCompModal.compId, renameCompModal.name.trim());
      setItem((prev) => {
        if (!prev || !prev.compartments) return prev;
        const compartments = prev.compartments.map((c) =>
          c.id === renameCompModal.compId ? { ...c, name: renamed.name } : c
        );
        return { ...prev, compartments } as Item;
      });
      setRenameCompModal({ show: false });
    } catch (err) {
      alert('Fehler beim Umbenennen des Fachs');
    }
  };

  const handleDeleteStorageItem = async () => {
    if (!item) return;

    const itemLabel = item.data.bezeichnung || item.data.id || 'dieses Objekt';
    const firstConfirm = window.confirm(`Lagerobjekt "${itemLabel}" wirklich löschen?\nAlle Fächer und Inhalte werden ebenfalls gelöscht.`);
    if (!firstConfirm) return;

    const secondConfirm = window.confirm('Bitte erneut bestätigen: Soll dieses Lagerobjekt endgültig gelöscht werden?');
    if (!secondConfirm) return;

    try {
      await inventoryService.deleteItem(item.id);
      const category = searchParams.get('category');
      navigate(category ? `/inventory?category=${category}` : '/inventory');
    } catch (err) {
      alert('Fehler beim Löschen des Lagerobjekts');
    }
  };

  const handleOpenSettings = () => {
    if (!item) return;
    const unitName = item.data.bezeichnung || item.data.id || '';
    const qty = item.data.quantity || 1;
    const fallbackLocation = item.data.storageLocation || item.data.standort || DEFAULT_STORAGE_LOCATIONS[0];
    const insts = (item.data.instances || []).map((instance) => ({
      ...instance,
      storageLocation: instance.storageLocation || fallbackLocation,
    }));
    const storageLocation = item.data.storageLocation || item.data.standort || '';
    const uniqueInstanceLocations = Array.from(new Set(insts
      .map((instance) => instance.storageLocation?.trim())
      .filter((location): location is string => Boolean(location))));
    const locationMode: 'shared' | 'individual' = qty > 1 && uniqueInstanceLocations.length > 1 ? 'individual' : 'shared';
    const resolvedStorageLocation = uniqueInstanceLocations.length === 1 ? uniqueInstanceLocations[0] : (storageLocation || fallbackLocation);
    const autoRun = insts.length > 0 && insts.every((i: any) => i.identifier?.match(/\d+$/) && i.type === 'generic');
    setSettingsForm({ unitName, quantity: qty, autoRunning: autoRun, storageLocation: resolvedStorageLocation, locationMode, instances: insts });
    setSettingsModal(true);
  };

  const saveCustomLocations = (locations: string[]) => {
    const normalized = Array.from(new Set(locations.map((location) => location.trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, 'de'));
    setCustomLocations(normalized);
    localStorage.setItem(STORAGE_LOCATIONS_KEY, JSON.stringify(normalized));
  };

  const settingsLocationOptions = useMemo(() => {
    const known = new Set<string>([...DEFAULT_STORAGE_LOCATIONS, ...customLocations]);
    const itemLocation = item?.data?.storageLocation || item?.data?.standort;
    if (itemLocation) {
      known.add(itemLocation);
    }
    settingsForm.instances.forEach((instance) => {
      if (instance.storageLocation) {
        known.add(instance.storageLocation);
      }
    });
    if (settingsForm.storageLocation) {
      known.add(settingsForm.storageLocation);
    }
    return Array.from(known).sort((a, b) => a.localeCompare(b, 'de'));
  }, [customLocations, item?.data?.storageLocation, item?.data?.standort, settingsForm.instances, settingsForm.storageLocation]);

  const handleAddLocationOption = () => {
    const input = window.prompt('Neuen Standort eingeben:');
    if (!input) return;
    const newLocation = input.trim();
    if (!newLocation) return;
    const exists = settingsLocationOptions.some((location) => location.toLowerCase() === newLocation.toLowerCase());
    if (!exists) {
      saveCustomLocations([...customLocations, newLocation]);
    }
    setSettingsForm((prev) => ({ ...prev, storageLocation: newLocation }));
  };

  const exportInventoryPdf = () => {
    if (!item) return;

    const title = item.data.bezeichnung || item.data.id || 'Inventarliste';
    const generatedAt = new Date().toLocaleString('de-DE');
    const generatedBy = user?.username || 'Unbekannt';

    const escapeHtml = (value: any) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    const renderRows = (items: any[] = []) => {
      if (!items.length) {
        return `<tr><td colspan="3" class="empty">Keine Einträge</td></tr>`;
      }
      return items.map((entry) => `
        <tr>
          <td>${escapeHtml(entry.name || entry.itemId || '-')}</td>
          <td>${escapeHtml(entry.qty ?? '-')}</td>
          <td>&nbsp;</td>
        </tr>
      `).join('');
    };

    const sections = (item.compartments || []).map((comp: any) => {
      const mainTable = `
        <div class="table-wrap">
          <table>
            <thead>
              <tr><th>Item</th><th>Menge</th><th>Notizen</th></tr>
            </thead>
            <tbody>
              ${renderRows(comp.contents?.items || [])}
            </tbody>
          </table>
        </div>
      `;

      const modulePackTables = (comp.contents?.modulePacks || []).map((mp: any) => `
        <div class="module-pack">
          <h4>Modultasche: ${escapeHtml(mp.name)}</h4>
          <div class="table-wrap">
            <table>
              <thead>
                <tr><th>Item</th><th>Menge</th><th>Notizen</th></tr>
              </thead>
              <tbody>
                ${renderRows(mp.items || [])}
              </tbody>
            </table>
          </div>
        </div>
      `).join('');

      return `
        <section class="compartment">
          <h3>${escapeHtml(comp.name)}</h3>
          ${mainTable}
          ${modulePackTables}
        </section>
      `;
    }).join('');

    const html = `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Inventarliste - ${escapeHtml(title)}</title>
        <style>
          @page { size: A4 portrait; margin: 12mm; }
          * { box-sizing: border-box; }
          body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 0; font-size: 12px; }
          .meta { margin-bottom: 10px; border: 1px solid #d8dbe3; border-radius: 6px; padding: 8px 10px; }
          .meta .org { font-weight: 700; margin-bottom: 3px; }
          .meta .line { font-size: 11px; color: #334155; }
          h1 { margin: 0 0 8px; font-size: 22px; }
          .subline { margin-bottom: 10px; color: #334155; font-size: 11px; }
          .compartment { margin: 0 0 12px; page-break-inside: avoid; break-inside: avoid; }
          .compartment h3 { margin: 0 0 6px; font-size: 15px; border-bottom: 1px solid #ddd; padding-bottom: 3px; }
          .module-pack { margin-top: 8px; padding-left: 8px; border-left: 3px solid #d1d9e6; page-break-inside: avoid; break-inside: avoid; }
          .module-pack h4 { margin: 0 0 4px; font-size: 12px; }
          .table-wrap { width: 100%; overflow: hidden; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          th, td { border: 1px solid #dbe0ea; padding: 4px 6px; vertical-align: top; word-wrap: break-word; }
          th { background: #f3f6fb; font-weight: 700; }
          th:nth-child(1), td:nth-child(1) { width: 58%; text-align: left; }
          th:nth-child(2), td:nth-child(2) { width: 12%; text-align: center; }
          th:nth-child(3), td:nth-child(3) { width: 30%; text-align: left; }
          .empty { text-align: center; color: #6b7280; }
          .footer { margin-top: 8px; font-size: 10px; color: #64748b; }
        </style>
      </head>
      <body>
        <div class="meta">
          <div class="org">BRK KV Dachau</div>
          <div class="line">Automatisch generierter Ausdruck</div>
          <div class="line">Gedruckt von: ${escapeHtml(generatedBy)} · Datum/Zeit: ${escapeHtml(generatedAt)}</div>
        </div>
        <h1>${escapeHtml(title)}</h1>
        <div class="subline">Kategorie: ${escapeHtml(item.category?.name || '-')}</div>
        ${sections || '<p>Keine Fächer vorhanden</p>'}
        <div class="footer">Inventarliste / Backup-Ausdruck</div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Popup blockiert. Bitte Popups erlauben und erneut versuchen.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);
  };

  const handleSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item || settingsForm.quantity < 1) return;
    const unitName = settingsForm.unitName.trim();
    if (!unitName) {
      alert('Bitte einen Namen eingeben');
      return;
    }
    const sharedStorageLocation = settingsForm.storageLocation.trim();
    if (settingsForm.locationMode === 'shared' && !sharedStorageLocation) {
      alert('Bitte einen Standort eingeben');
      return;
    }
    try {
      let instances = settingsForm.instances;
      let quantity = settingsForm.quantity;
      if (settingsForm.autoRunning) {
        instances = Array.from({ length: settingsForm.quantity }, (_, i) => ({
          id: i + 1,
          identifier: `${unitName || 'Exemplar'} ${i + 1}`,
          type: 'generic'
        }));
        quantity = instances.length;
      } else {
        quantity = instances.length > 0 ? instances.length : Math.max(1, settingsForm.quantity);
      }

      let storageLocation = sharedStorageLocation;
      if (settingsForm.locationMode === 'individual' && quantity > 1) {
        const missingLocation = instances.find((instance) => !instance.storageLocation?.trim());
        if (missingLocation) {
          alert('Bitte für jedes Exemplar einen Standort wählen');
          return;
        }
        const uniqueLocations = Array.from(new Set(instances
          .map((instance) => instance.storageLocation?.trim())
          .filter((location): location is string => Boolean(location))));
        storageLocation = uniqueLocations.length <= 1 ? (uniqueLocations[0] || sharedStorageLocation) : 'Individuell';
      } else {
        storageLocation = sharedStorageLocation;
        instances = instances.map((instance) => ({ ...instance, storageLocation }));
      }

      if (!storageLocation) {
        alert('Bitte einen Standort eingeben');
        return;
      }

      const updated = await inventoryService.updateItem(item.id, {
        data: { ...item.data, bezeichnung: unitName, quantity, instances, storageLocation }
      });
      setItem({ ...item, data: updated.data });
      setSettingsModal(false);
    } catch (err) {
      alert('Fehler beim Speichern der Einstellungen');
    }
  };

  const addInstance = () => {
    const newId = Math.max(0, ...settingsForm.instances.map((i) => i.id)) + 1;
    const newInstances = [...settingsForm.instances, { id: newId, type: 'generic', identifier: '', storageLocation: settingsForm.storageLocation || settingsLocationOptions[0] || DEFAULT_STORAGE_LOCATIONS[0] }];
    setSettingsForm((prev) => ({
      ...prev,
      instances: newInstances,
      quantity: !prev.autoRunning ? newInstances.length : prev.quantity
    }));
  };

  const removeInstance = (idx: number) => {
    const newInstances = settingsForm.instances.filter((_, i) => i !== idx);
    setSettingsForm((prev) => ({
      ...prev,
      instances: newInstances,
      quantity: !prev.autoRunning ? newInstances.length : prev.quantity
    }));
  };

  const updateInstance = (idx: number, field: 'type' | 'identifier' | 'storageLocation', value: string) => {
    setSettingsForm((prev) => ({
      ...prev,
      instances: prev.instances.map((inst, i) =>
        i === idx ? { ...inst, [field]: value } : inst
      )
    }));
  };

  if (loading) return <div className="loading">Lädt...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!item) return <div>Item nicht gefunden</div>;

  return (
    <div className="item-detail">
      <div className="detail-header">
        <button onClick={() => {
          const category = searchParams.get('category');
          navigate(category ? `/inventory?category=${category}` : '/inventory');
        }} className="btn-back">← Zurück</button>
        <h1 style={{ flex: 1 }}>{item.data.bezeichnung || item.data.id}</h1>
        <span className="category-badge">{item.category?.name}</span>
        {isAdmin && (
          <>
            <button className="btn-secondary" style={{ marginLeft: '0.5rem' }} onClick={handleOpenSettings}>
              Eigenschaften
            </button>
            <button className="btn-secondary" style={{ marginLeft: '0.5rem' }} onClick={exportInventoryPdf}>
              Export PDF
            </button>
            <button className="btn-delete" style={{ marginLeft: '0.5rem' }} onClick={handleDeleteStorageItem}>
              Item löschen
            </button>
          </>
        )}
      </div>

      {(() => {
        const selected = (item.data.instances || []).find((instance) => instance.identifier === selectedInstance);
        const displayLocation = selected?.storageLocation || item.data.storageLocation || item.data.standort;
        if (!displayLocation) return null;
        return (
        <div className="item-location-banner">
          <span className="item-location-label">Standort</span>
          <span className="item-location-value">{displayLocation}</span>
        </div>
        );
      })()}

      {/* Instance selector for items with multiple instances */}
      {item.data.instances && item.data.instances.length > 0 && (
        <div className="instance-selector">
          <label htmlFor="instance-select">
            {item.data.instances[0].type === 'license_plate' ? 'Fahrzeug auswählen:' : 'Exemplar auswählen:'}
          </label>
          <select 
            id="instance-select"
            value={selectedInstance}
            onChange={(e) => setSelectedInstance(e.target.value)}
          >
            {item.data.instances.map((instance) => (
              <option key={instance.id} value={instance.identifier}>
                {instance.identifier}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="compartments">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h2>Fächer</h2>
            {item?.compartments && item.compartments.length > 0 && (
              <button 
                className="btn-secondary" 
                style={{ width: 'auto', fontSize: '0.9rem', padding: '0.5rem 1rem' }} 
                onClick={toggleAllCompartments}
              >
                {allAreCollapsed() ? 'Alle ausklappen' : 'Alle einklappen'}
              </button>
            )}
          </div>
          {isAdmin && (
            <button className="btn-primary" style={{ width: 'auto' }} onClick={() => setNewCompModal(true)}>Neues Fach anlegen</button>
          )}
        </div>
        {item.compartments && item.compartments.length > 0 ? (
          item.compartments.map((comp: Compartment) => {
            const isCollapsed = collapsedCompartments[comp.id];
            return (
            <div key={comp.id} className="compartment">
              <div className="compartment-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 
                  onClick={() => toggleCompartment(comp.id)} 
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}
                >
                  <span className="toggle-icon">{isCollapsed ? '▶' : '▼'}</span>
                  {comp.name}
                </h3>
                {isAdmin && (
                  <div className="compartment-actions">
                    <button className="btn-secondary" onClick={(e) => { e.stopPropagation(); handleOpenAdd(comp.id, comp.name); }}>Item hinzufügen</button>
                    <button className="btn-secondary" onClick={(e) => { e.stopPropagation(); setAddModulePackModal({ show: true, compId: comp.id, compName: comp.name }); setNewModulePackName(''); }}>Modul hinzufügen</button>
                    <button className="btn-secondary" onClick={(e) => { e.stopPropagation(); setRenameCompModal({ show: true, compId: comp.id, name: comp.name }); }}>Fach umbenennen</button>
                    <button className="btn-delete" onClick={(e) => { e.stopPropagation(); handleDeleteCompartment(comp.id, comp.name); }}>Fach löschen</button>
                  </div>
                )}
              </div>
              
              {/* Main compartment items */}
              {!isCollapsed && (
              <>
              {comp.contents.items && comp.contents.items.length > 0 && (
                <table className={`items-table ${!isAdmin ? 'items-table-user' : ''}`}>
                  <thead>
                    <tr>
                      {isAdmin && <th style={{ width: '8%' }}>Pos</th>}
                      <th>Item</th>
                      <th>Menge</th>
                      <th>Ablaufdatum</th>
                      <th>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isAdmin ? (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(event) => handleCompartmentDragEnd(comp.id, comp.contents.items, event)}
                      >
                        <SortableContext
                          items={comp.contents.items.map((contentItem, idx) => getMainSortableId(comp.id, idx, contentItem))}
                          strategy={verticalListSortingStrategy}
                        >
                          {comp.contents.items.map((contentItem, idx) => {
                            const isEditingQty = editingQty[comp.id]?.[idx];
                            const suggestionKey = `${comp.id}-${idx}`;
                            const rowId = getMainSortableId(comp.id, idx, contentItem);

                            return (
                              <SortableTableRow
                                key={`${comp.id}-${contentItem.itemId || contentItem.name}-${idx}`}
                                id={rowId}
                              >
                                {({ attributes, listeners }) => (
                                  <>
                                    <td style={{ textAlign: 'center', fontWeight: 600 }}>
                                      <div className="drag-cell">
                                        <button
                                          type="button"
                                          className="drag-handle"
                                          title="Ziehen zum Sortieren"
                                          aria-label="Ziehen zum Sortieren"
                                          {...attributes}
                                          {...listeners}
                                        >
                                          ⋮⋮
                                        </button>
                                        <span>{idx + 1}</span>
                                      </div>
                                    </td>
                                    <td style={{ position: 'relative' }}>
                                      {isEditingQty ? (
                                        <div style={{ position: 'relative' }}>
                                          <input
                                            type="text"
                                            value={itemNameInputs[comp.id]?.[idx] || ''}
                                            onChange={(e) => handleEditItemNameChange(comp.id, idx, e.target.value)}
                                            onFocus={() => itemNameInputs[comp.id]?.[idx] && editSuggestions.length > 0 && setShowEditSuggestions(suggestionKey)}
                                            placeholder="Item-Name"
                                            style={{ width: '100%', padding: '0.25rem' }}
                                            autoComplete="off"
                                          />
                                          {showEditSuggestions === suggestionKey && editSuggestions.length > 0 && (
                                            <div className="autocomplete-suggestions" style={{ position: 'absolute', zIndex: 1000 }}>
                                              {editSuggestions.map((suggestion) => (
                                                <div
                                                  key={suggestion.id}
                                                  className="autocomplete-item"
                                                  onClick={() => handleSelectEditSuggestion(comp.id, idx, suggestion.name)}
                                                >
                                                  {suggestion.name}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        contentItem.name
                                      )}
                                    </td>
                                    <td>
                                      {isEditingQty ? (
                                        <input
                                          type="number"
                                          min="1"
                                          value={qtyInputs[comp.id]?.[idx] || ''}
                                          onChange={(e) => setQtyInputs((prev) => ({
                                            ...prev,
                                            [comp.id]: { ...prev[comp.id], [idx]: parseInt(e.target.value) || 1 }
                                          }))}
                                          style={{ width: '60px', padding: '0.25rem' }}
                                        />
                                      ) : (
                                        contentItem.qty
                                      )}
                                    </td>
                                    <td>
                                      <ExpiryDateCell
                                        nextExpiry={(contentItem as any).nextExpiry || null}
                                        onClick={() => handleExpiryClick(comp.id, idx, contentItem as CompartmentItem)}
                                      />
                                    </td>
                                    <td>
                                      <button
                                        className="btn-order-small"
                                        onClick={() => handleOrderClick(contentItem.name, comp.name)}
                                      >
                                        Bestellen
                                      </button>
                                      {!isEditingQty && (
                                        <button
                                          className="btn-secondary-small"
                                          onClick={() => handleEditQty(comp.id, idx, contentItem.qty, contentItem.name)}
                                          style={{ marginLeft: 8 }}
                                          title="Bearbeiten"
                                        >
                                          ✎
                                        </button>
                                      )}
                                      {isEditingQty && (
                                        <>
                                          <button
                                            className="btn-secondary-small"
                                            onClick={() => handleSaveQty(comp.id, idx)}
                                            style={{ marginLeft: 8, backgroundColor: '#28a745', color: 'white' }}
                                            title="Speichern"
                                          >
                                            ✓
                                          </button>
                                          <button
                                            className="btn-secondary-small"
                                            onClick={() => handleCancelEditQty(comp.id, idx)}
                                            style={{ marginLeft: 4 }}
                                            title="Abbrechen"
                                          >
                                            ✕
                                          </button>
                                        </>
                                      )}
                                      {!isEditingQty && (
                                        <>
                                          {/* LEGACY_REORDER_CONTROLS_START (main compartment): replaced by drag-and-drop; keep for quick re-enable
                                          <button
                                            className="btn-secondary-small"
                                            onClick={() => handleMoveItem(comp.id, idx, 'up')}
                                            disabled={idx === 0}
                                            style={{ marginLeft: 8, padding: '0.35rem 0.5rem', opacity: idx === 0 ? 0.5 : 1 }}
                                            title="Nach oben"
                                          >
                                            ↑
                                          </button>
                                          <button
                                            className="btn-secondary-small"
                                            onClick={() => handleMoveItem(comp.id, idx, 'down')}
                                            disabled={idx === comp.contents.items.length - 1}
                                            style={{ marginLeft: 4, padding: '0.35rem 0.5rem', opacity: idx === comp.contents.items.length - 1 ? 0.5 : 1 }}
                                            title="Nach unten"
                                          >
                                            ↓
                                          </button>
                                          <input
                                            type="number"
                                            min="1"
                                            max={comp.contents.items.length}
                                            value={positionInputs[comp.id]?.[idx] || ''}
                                            onChange={(e) => setPositionInputs((prev) => ({
                                              ...prev,
                                              [comp.id]: { ...prev[comp.id], [idx]: e.target.value }
                                            }))}
                                            placeholder="Pos"
                                            style={{ marginLeft: 4, width: '70px', padding: '0.5rem', fontSize: '0.9rem' }}
                                          />
                                          <button
                                            className="btn-secondary-small"
                                            onClick={() => handleMoveToPosition(comp.id, idx, positionInputs[comp.id]?.[idx] || '')}
                                            disabled={!(positionInputs[comp.id]?.[idx] || '')}
                                            style={{ marginLeft: 4, padding: '0.35rem 0.7rem' }}
                                            title="Zu Position verschieben"
                                          >
                                            OK
                                          </button>
                                          LEGACY_REORDER_CONTROLS_END */}
                                          <button
                                            className="btn-delete-small"
                                            onClick={() => handleDeleteItem(comp.id, idx)}
                                            style={{ marginLeft: 8 }}
                                          >
                                            Löschen
                                          </button>
                                        </>
                                      )}
                                    </td>
                                  </>
                                )}
                              </SortableTableRow>
                            );
                          })}
                        </SortableContext>
                      </DndContext>
                    ) : (
                      comp.contents.items.map((contentItem, idx) => {
                        return (
                          <tr key={`${comp.id}-${contentItem.itemId || contentItem.name}-${idx}`}>
                            <td style={{ position: 'relative' }}>
                              {contentItem.name}
                            </td>
                            <td>{contentItem.qty}</td>
                            <td>
                              <ExpiryDateCell
                                nextExpiry={(contentItem as any).nextExpiry || null}
                                onClick={() => handleExpiryClick(comp.id, idx, contentItem as CompartmentItem)}
                              />
                            </td>
                            <td>
                              <button
                                className="btn-order-small"
                                onClick={() => handleOrderClick(contentItem.name, comp.name)}
                              >
                                Bestellen
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}

              {/* Module Packs (Sub-compartments) */}
              {comp.contents.modulePacks && comp.contents.modulePacks.length > 0 && (
                <div className="module-packs">
                  {comp.contents.modulePacks.map((modulePack: any, mpIdx: number) => (
                    <div key={`${comp.id}-mp-${modulePack.name}-${mpIdx}`} className="module-pack">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <h4 className="module-pack-title" style={{ margin: 0 }}>{modulePack.name}</h4>
                        {isAdmin && (
                          <div>
                            <button className="btn-secondary-small" onClick={() => handleOpenAdd(comp.id, comp.name, modulePack.name)} style={{ marginRight: '0.5rem' }}>Item hinzufügen</button>
                            <button className="btn-secondary-small" onClick={() => setRenameModulePackModal({ show: true, compId: comp.id, oldName: modulePack.name, newName: modulePack.name })} style={{ marginRight: '0.5rem' }}>Umbenennen</button>
                            <button className="btn-delete-small" onClick={() => handleDeleteModulePack(comp.id, modulePack.name)}>Modultasche löschen</button>
                          </div>
                        )}
                      </div>
                      <table className={`items-table module-pack-table ${!isAdmin ? 'items-table-user' : ''}`}>
                        <thead>
                          <tr>
                            {isAdmin && <th style={{ width: '8%' }}>Pos</th>}
                            <th>Item</th>
                            <th>Menge</th>
                            <th>Ablaufdatum</th>
                            <th>Aktion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isAdmin ? (
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragEnd={(event) => handleModulePackDragEnd(comp.id, modulePack.name, modulePack.items, event)}
                            >
                              <SortableContext
                                items={modulePack.items.map((mpItem: any, idx: number) =>
                                  getModulePackSortableId(comp.id, modulePack.name, idx, mpItem)
                                )}
                                strategy={verticalListSortingStrategy}
                              >
                                {modulePack.items.map((mpItem: any, idx: number) => {
                                  const mpKey = `${comp.id}-${modulePack.name}`;
                                  const isEditingMpQty = editingModulePackQty[mpKey]?.[idx];
                                  const mpSuggestionKey = `${mpKey}-${idx}`;
                                  const rowId = getModulePackSortableId(comp.id, modulePack.name, idx, mpItem);

                                  return (
                                    <SortableTableRow
                                      key={`${comp.id}-${modulePack.name}-${mpItem.itemId || mpItem.name}-${idx}`}
                                      id={rowId}
                                    >
                                      {({ attributes, listeners }) => (
                                        <>
                                          <td style={{ textAlign: 'center', fontWeight: 600 }}>
                                            <div className="drag-cell">
                                              <button
                                                type="button"
                                                className="drag-handle"
                                                title="Ziehen zum Sortieren"
                                                aria-label="Ziehen zum Sortieren"
                                                {...attributes}
                                                {...listeners}
                                              >
                                                ⋮⋮
                                              </button>
                                              <span>{idx + 1}</span>
                                            </div>
                                          </td>
                                          <td style={{ position: 'relative' }}>
                                            {isEditingMpQty ? (
                                              <div style={{ position: 'relative' }}>
                                                <input
                                                  type="text"
                                                  value={modulePackItemNameInputs[mpKey]?.[idx] || ''}
                                                  onChange={(e) => handleEditModulePackItemNameChange(comp.id, modulePack.name, idx, e.target.value)}
                                                  onFocus={() => modulePackItemNameInputs[mpKey]?.[idx] && editSuggestions.length > 0 && setShowEditSuggestions(mpSuggestionKey)}
                                                  placeholder="Item-Name"
                                                  style={{ width: '100%', padding: '0.25rem' }}
                                                  autoComplete="off"
                                                />
                                                {showEditSuggestions === mpSuggestionKey && editSuggestions.length > 0 && (
                                                  <div className="autocomplete-suggestions" style={{ position: 'absolute', zIndex: 1000 }}>
                                                    {editSuggestions.map((suggestion) => (
                                                      <div
                                                        key={suggestion.id}
                                                        className="autocomplete-item"
                                                        onClick={() => handleSelectEditModulePackSuggestion(comp.id, modulePack.name, idx, suggestion.name)}
                                                      >
                                                        {suggestion.name}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            ) : (
                                              mpItem.name
                                            )}
                                          </td>
                                          <td>
                                            {isEditingMpQty ? (
                                              <input
                                                type="number"
                                                min="1"
                                                value={modulePackQtyInputs[mpKey]?.[idx] || ''}
                                                onChange={(e) => setModulePackQtyInputs((prev) => ({
                                                  ...prev,
                                                  [mpKey]: { ...prev[mpKey], [idx]: parseInt(e.target.value) || 1 }
                                                }))}
                                                style={{ width: '60px', padding: '0.25rem' }}
                                              />
                                            ) : (
                                              mpItem.qty
                                            )}
                                          </td>
                                          <td>
                                            <ExpiryDateCell
                                              nextExpiry={(mpItem as any).nextExpiry || null}
                                              onClick={() => handleExpiryClick(comp.id, idx, mpItem as CompartmentItem, true, modulePack.name)}
                                            />
                                          </td>
                                          <td>
                                            <button
                                              className="btn-order-small"
                                              onClick={() => handleOrderClick(mpItem.name, `${comp.name} - ${modulePack.name}`)}
                                            >
                                              Bestellen
                                            </button>
                                            {!isEditingMpQty && (
                                              <button
                                                className="btn-secondary-small"
                                                onClick={() => handleEditModulePackQty(comp.id, modulePack.name, idx, mpItem.qty, mpItem.name)}
                                                style={{ marginLeft: 8 }}
                                                title="Bearbeiten"
                                              >
                                                ✎
                                              </button>
                                            )}
                                            {isEditingMpQty && (
                                              <>
                                                <button
                                                  className="btn-secondary-small"
                                                  onClick={() => handleSaveModulePackQty(comp.id, modulePack.name, idx)}
                                                  style={{ marginLeft: 8, backgroundColor: '#28a745', color: 'white' }}
                                                  title="Speichern"
                                                >
                                                  ✓
                                                </button>
                                                <button
                                                  className="btn-secondary-small"
                                                  onClick={() => handleCancelEditModulePackQty(comp.id, modulePack.name, idx)}
                                                  style={{ marginLeft: 4 }}
                                                  title="Abbrechen"
                                                >
                                                  ✕
                                                </button>
                                              </>
                                            )}
                                            {!isEditingMpQty && (
                                              <>
                                                {/* LEGACY_REORDER_CONTROLS_START (module pack): replaced by drag-and-drop; keep for quick re-enable
                                                <button
                                                  className="btn-secondary-small"
                                                  onClick={() => handleMoveModulePackItem(comp.id, modulePack.name, idx, 'up')}
                                                  disabled={idx === 0}
                                                  style={{ marginLeft: 8, padding: '0.35rem 0.5rem', opacity: idx === 0 ? 0.5 : 1 }}
                                                  title="Nach oben"
                                                >
                                                  ↑
                                                </button>
                                                <button
                                                  className="btn-secondary-small"
                                                  onClick={() => handleMoveModulePackItem(comp.id, modulePack.name, idx, 'down')}
                                                  disabled={idx === modulePack.items.length - 1}
                                                  style={{ marginLeft: 4, padding: '0.35rem 0.5rem', opacity: idx === modulePack.items.length - 1 ? 0.5 : 1 }}
                                                  title="Nach unten"
                                                >
                                                  ↓
                                                </button>
                                                <input
                                                  type="number"
                                                  min="1"
                                                  max={modulePack.items.length}
                                                  value={modulePackPositionInputs[mpKey]?.[idx] || ''}
                                                  onChange={(e) => setModulePackPositionInputs((prev) => ({
                                                    ...prev,
                                                    [mpKey]: { ...prev[mpKey], [idx]: e.target.value }
                                                  }))}
                                                  placeholder="Pos"
                                                  style={{ marginLeft: 4, width: '70px', padding: '0.5rem', fontSize: '0.9rem' }}
                                                />
                                                <button
                                                  className="btn-secondary-small"
                                                  onClick={() => handleMoveModulePackItemToPosition(comp.id, modulePack.name, idx, modulePackPositionInputs[mpKey]?.[idx] || '')}
                                                  disabled={!(modulePackPositionInputs[mpKey]?.[idx] || '')}
                                                  style={{ marginLeft: 4, padding: '0.35rem 0.7rem' }}
                                                  title="Zu Position verschieben"
                                                >
                                                  OK
                                                </button>
                                                LEGACY_REORDER_CONTROLS_END */}
                                                <button
                                                  className="btn-delete-small"
                                                  onClick={() => handleDeleteModulePackItem(comp.id, modulePack.name, idx)}
                                                  style={{ marginLeft: 8 }}
                                                >
                                                  Löschen
                                                </button>
                                              </>
                                            )}
                                          </td>
                                        </>
                                      )}
                                    </SortableTableRow>
                                  );
                                })}
                              </SortableContext>
                            </DndContext>
                          ) : (
                            modulePack.items.map((mpItem: any, idx: number) => (
                              <tr key={`${comp.id}-${modulePack.name}-${mpItem.itemId || mpItem.name}-${idx}`}>
                                <td style={{ position: 'relative' }}>{mpItem.name}</td>
                                <td>{mpItem.qty}</td>
                                <td>
                                  <ExpiryDateCell
                                    nextExpiry={(mpItem as any).nextExpiry || null}
                                    onClick={() => handleExpiryClick(comp.id, idx, mpItem as CompartmentItem, true, modulePack.name)}
                                  />
                                </td>
                                <td>
                                  <button
                                    className="btn-order-small"
                                    onClick={() => handleOrderClick(mpItem.name, `${comp.name} - ${modulePack.name}`)}
                                  >
                                    Bestellen
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
              </>
              )}
            </div>
            );
          })
        ) : (
          <p>Keine Fächer vorhanden</p>
        )}
      </div>

      {orderModal.show && (
        <div className="modal-overlay" onClick={() => setOrderModal({ show: false, itemName: '' })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Item bestellen</h2>
            <form onSubmit={handleOrderSubmit} className="order-dialog-form">
              <div className="order-dialog-info" aria-label="Bestellinformationen">
                <div className="order-dialog-info-item">
                  <span className="order-dialog-info-label">Item</span>
                  <span className="order-dialog-info-value">{orderModal.itemName}</span>
                </div>
                {orderModal.compartment && (
                  <div className="order-dialog-info-item">
                    <span className="order-dialog-info-label">Fach</span>
                    <span className="order-dialog-info-value">{orderModal.compartment}</span>
                  </div>
                )}
                <div className="order-dialog-info-item">
                  <span className="order-dialog-info-label">Zielort</span>
                  <span className="order-dialog-info-value">{orderForm.location}</span>
                </div>
              </div>
              {item?.data.instances && item.data.instances.length > 0 && (
                <div className="form-group">
                  <label>
                    {item.data.instances[0].type === 'license_plate' ? 'Fahrzeug:' : 'Exemplar:'}
                  </label>
                  <select
                    value={selectedInstance}
                    onChange={(e) => setSelectedInstance(e.target.value)}
                    required
                  >
                    {item.data.instances.map((instance) => (
                      <option key={instance.id} value={instance.identifier}>
                        {instance.identifier}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Menge:</label>
                <input
                  type="number"
                  min="1"
                  value={orderForm.quantity}
                  onChange={(e) => setOrderForm({ ...orderForm, quantity: parseInt(e.target.value) })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Notizen:</label>
                <textarea
                  value={orderForm.notes}
                  onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setOrderModal({ show: false, itemName: '' })}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-primary">
                  Bestellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {addModal.show && (
        <div className="modal-overlay" onClick={() => setAddModal({ show: false })}>          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Item hinzufügen</h2>
            <form onSubmit={handleAddSubmit}>
              <div className="form-group">
                <label>Fach:</label>
                <input type="text" value={addModal.compName || ''} disabled />
              </div>
              {addModal.modulePackName && (
                <div className="form-group">
                  <label>Modultasche:</label>
                  <input type="text" value={addModal.modulePackName} disabled />
                </div>
              )}
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Item:</label>
                <input
                  ref={addItemInputRef}
                  type="text"
                  value={addForm.itemName}
                  onChange={(e) => handleItemNameChange(e.target.value)}
                  onFocus={() => addForm.itemName && itemSuggestions.length > 0 && setShowSuggestions(true)}
                  placeholder="z. B. Kompresse 10x10"
                  required
                  autoComplete="off"
                />
                {showSuggestions && itemSuggestions.length > 0 && (
                  <div className="autocomplete-suggestions">
                    {itemSuggestions.map((suggestion) => (
                      <div
                        key={suggestion.id}
                        className="autocomplete-item"
                        onClick={() => handleSelectSuggestion(suggestion)}
                      >
                        {suggestion.name}
                      </div>
                    ))}
                  </div>
                )}
                {addForm.itemName && !availableItems.some(ai => ai.name.toLowerCase() === addForm.itemName.toLowerCase()) && (
                  <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
                    Neues Item wird erstellt
                  </small>
                )}
              </div>
              <div className="form-group">
                <label>Menge:</label>
                <input
                  type="number"
                  min="1"
                  value={addForm.qty}
                  onChange={(e) => setAddForm({ ...addForm, qty: parseInt(e.target.value) || 1 })}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setAddModal({ show: false })}>Abbrechen</button>
                <button type="submit" className="btn-primary">Hinzufügen</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {renameCompModal.show && (
        <div className="modal-overlay" onClick={() => setRenameCompModal({ show: false })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Fach umbenennen</h2>
            <form onSubmit={handleRenameCompartmentSubmit}>
              <div className="form-group">
                <label>Neuer Name:</label>
                <input
                  type="text"
                  value={renameCompModal.name || ''}
                  onChange={(e) => setRenameCompModal((prev) => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setRenameCompModal({ show: false })}>Abbrechen</button>
                <button type="submit" className="btn-primary">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {renameModulePackModal.show && (
        <div className="modal-overlay" onClick={() => setRenameModulePackModal({ show: false })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Modultasche umbenennen</h2>
            <form onSubmit={handleRenameModulePackSubmit}>
              <div className="form-group">
                <label>Neuer Name:</label>
                <input
                  type="text"
                  value={renameModulePackModal.newName || ''}
                  onChange={(e) => setRenameModulePackModal((prev) => ({ ...prev, newName: e.target.value }))}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setRenameModulePackModal({ show: false })}>Abbrechen</button>
                <button type="submit" className="btn-primary">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {settingsModal && (
        <div className="modal-overlay" onClick={() => setSettingsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Einheit Eigenschaften</h2>
            <form onSubmit={handleSettingsSubmit}>
              <div className="form-group">
                <label>Name:</label>
                <input
                  type="text"
                  value={settingsForm.unitName}
                  onChange={(e) => setSettingsForm({ ...settingsForm, unitName: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Anzahl:</label>
                <input
                  type="number"
                  min="1"
                  value={settingsForm.quantity}
                  onChange={(e) => setSettingsForm({ ...settingsForm, quantity: Math.max(1, parseInt(e.target.value) || 1), locationMode: (parseInt(e.target.value) || 1) > 1 ? settingsForm.locationMode : 'shared' })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Standort:</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select
                  value={settingsForm.storageLocation}
                  onChange={(e) => setSettingsForm({ ...settingsForm, storageLocation: e.target.value })}
                  style={{ flex: 1 }}
                  required
                >
                  <option value="">Standort wählen</option>
                  {settingsLocationOptions.map((location) => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
                <button type="button" className="btn-secondary" onClick={handleAddLocationOption}>
                  Standort hinzufügen
                </button>
                </div>
              </div>

              {settingsForm.quantity > 1 && (
                <div className="form-group">
                  <label>Standort-Modus:</label>
                  <select
                    value={settingsForm.locationMode}
                    onChange={(e) => setSettingsForm({ ...settingsForm, locationMode: e.target.value as 'shared' | 'individual' })}
                    disabled={settingsForm.autoRunning}
                  >
                    <option value="shared">Alle Exemplare gleicher Standort</option>
                    <option value="individual">Standort je Exemplar</option>
                  </select>
                  {settingsForm.autoRunning && (
                    <small style={{ color: '#666' }}>Bei laufenden Nummern ist nur ein gemeinsamer Standort aktiv.</small>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={settingsForm.autoRunning}
                    onChange={(e) => setSettingsForm({ ...settingsForm, autoRunning: e.target.checked, locationMode: e.target.checked ? 'shared' : settingsForm.locationMode })}
                  />
                  {" "}Laufende Nummern (Exemplar 1, Exemplar 2, ...)
                </label>
              </div>

              {!settingsForm.autoRunning && (
                <div className="form-group">
                  <label>Exemplare:</label>
                  {settingsForm.instances.length === 0 ? (
                    <p style={{ color: '#999', fontSize: '0.9rem' }}>Keine Exemplare definiert</p>
                  ) : (
                    <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '0.5rem' }}>
                      {settingsForm.instances.map((inst, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: settingsForm.locationMode === 'individual' && settingsForm.quantity > 1 ? '0.3fr 1fr 1fr auto' : '0.3fr 1fr auto', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'flex-end' }}>
                          <select
                            value={inst.type}
                            onChange={(e) => updateInstance(idx, 'type', e.target.value)}
                            style={{ padding: '0.5rem' }}
                          >
                            <option value="generic">Standard</option>
                            <option value="license_plate">Kennzeichen</option>
                          </select>
                          <input
                            type="text"
                            value={inst.identifier}
                            onChange={(e) => updateInstance(idx, 'identifier', e.target.value)}
                            placeholder="z.B. DAH-LK-150"
                            style={{ padding: '0.5rem' }}
                            required
                          />
                          {settingsForm.locationMode === 'individual' && settingsForm.quantity > 1 && (
                            <select
                              value={inst.storageLocation || ''}
                              onChange={(e) => updateInstance(idx, 'storageLocation', e.target.value)}
                              style={{ padding: '0.5rem' }}
                              required
                            >
                              <option value="">Standort</option>
                              {settingsLocationOptions.map((location) => (
                                <option key={location} value={location}>{location}</option>
                              ))}
                            </select>
                          )}
                          <button
                            type="button"
                            onClick={() => removeInstance(idx)}
                            className="btn-delete-small"
                          >
                            Entfernen
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={addInstance}
                    className="btn-secondary"
                    style={{ marginTop: '0.5rem' }}
                  >
                    Exemplar hinzufügen
                  </button>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" onClick={() => setSettingsModal(false)}>Abbrechen</button>
                <button type="submit" className="btn-primary">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {newCompModal && (
        <div className="modal-overlay" onClick={() => setNewCompModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Neues Fach anlegen</h2>
            <form onSubmit={handleCreateCompartment}>
              <div className="form-group">
                <label>Fachname:</label>
                <input
                  type="text"
                  value={newCompName}
                  onChange={(e) => setNewCompName(e.target.value)}
                  placeholder="z. B. Hauptfach"
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setNewCompModal(false)}>Abbrechen</button>
                <button type="submit" className="btn-primary">Anlegen</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {addModulePackModal.show && (
        <div className="modal-overlay" onClick={() => setAddModulePackModal({ show: false })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Neue Modultasche anlegen</h2>
            <form onSubmit={handleAddModulePack}>
              <div className="form-group">
                <label>Fach:</label>
                <input type="text" value={addModulePackModal.compName || ''} disabled />
              </div>
              <div className="form-group">
                <label>Modultasche Name:</label>
                <input
                  type="text"
                  value={newModulePackName}
                  onChange={(e) => setNewModulePackName(e.target.value)}
                  placeholder="z. B. Modultasche Dokumentation"
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setAddModulePackModal({ show: false })}>Abbrechen</button>
                <button type="submit" className="btn-primary">Anlegen</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {expiryDialog.show && expiryDialog.item && (
        <ExpiryDialog 
          item={expiryDialog.item}
          onSave={handleExpirySave}
          onClose={() => setExpiryDialog({ show: false })}
        />
      )}
    </div>
  );
}
